import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSignalRConnection } from '../../../hooks/useSignalRConnection';
import { getCurrentStatuses } from '../../../api/Attendance/attendance';
import { CountryView, UserView } from '../../../models/general';
import AppIcon from '../../../components/AppIcon';
import ReactCountryFlag from 'react-country-flag';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Spinner } from '../../../components/ui/Spinner';
import { BarChart3 } from 'lucide-react';
import { getFullUrl } from '../../../utils/url';

const STATUS_COLORS: Record<string, string> = {
    Available: 'bg-green-500',
    Break: 'bg-amber-600',
    Unavailable: 'bg-gray-400',
    Leave: 'bg-red-500',
    HalfDayLeave: 'bg-red-500',
    Holiday: 'bg-blue-500',
    Unknown: 'bg-gray-400',
};
const getStatusColor = (status: string) => STATUS_COLORS[status] ?? 'bg-gray-400';

const STATUS_TEXT_COLORS: Record<string, string> = {
    Available: 'text-green-600',
    Break: 'text-amber-600',
    Unavailable: 'text-gray-400',
    Leave: 'text-red-500',
    HalfDayLeave: 'text-red-500',
    Holiday: 'text-blue-500',
    Unknown: 'text-gray-400',
};
const getStatusTextColor = (status: string) => STATUS_TEXT_COLORS[status] ?? 'text-gray-400';

export default function Dashboard() {
    const { t } = useTranslation();
    const { userId, companyPrimaryColor } = useSelector((state: any) => ({
        userId: state.user.userId,
        companyPrimaryColor: state.user.companyPrimaryColor,
    }));

    const brandColor = companyPrimaryColor ?? 'var(--mowazaf-primary, #304EA1)';

    const [users, setUsers] = useState<UserView[]>([]);
    const [items, setItems] = useState<UserView[]>([]);
    const [countries, setCountries] = useState<CountryView[]>([]);
    const [loading, setLoading] = useState(false);
    const { subscribe, subscribeToAttendancePool } = useSignalRConnection();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await getCurrentStatuses();
            const payload = result.payload as UserView[];

            const uniqueCountries: CountryView[] = [];
            for (const u of payload) {
                if (u.country && !uniqueCountries.some((c) => c.iso === u.country.iso)) {
                    uniqueCountries.push(u.country);
                }
            }

            setUsers(payload);
            setItems(payload);
            setCountries(uniqueCountries);
        } catch (error: any) {
            console.error(error);
            if (error?.errors?.length > 0 && error.errors[0]?.description) {
                alert(error.errors[0].description);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // SignalR setup
    useEffect(() => {
        fetchData();

        // Subscribe to attendance pool via shared SignalR connection
        subscribeToAttendancePool().catch((err) =>
            console.error('Failed to subscribe to attendance pool:', err)
        );

        // Listen for availability updates
        const unsubscribe = subscribe('UsersAvailability', () => {
            fetchData();
        });

        return () => {
            unsubscribe();
        };
    }, [fetchData, subscribe, subscribeToAttendancePool]);

    const filterDataGlobal = (value: string) => {
        const lower = value.toLowerCase();
        setItems(
            users.filter(
                (u: any) =>
                    u.fullName?.toLowerCase().includes(lower) ||
                    u.country?.name?.toLowerCase().includes(lower),
            ),
        );
    };

    return (
        <PageLayout
            title={t('Colleagues Availability')}
            description={t('View Availabilities')}
            icon={BarChart3}
            accentColor="blue"
            maxWidth="1600px"
        >
            {/* Loading indicator */}
            {loading && items.length === 0 && (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <Spinner size="lg" className="text-primary" />
                    <div className="text-sm text-muted-foreground">{t('Loading attendance data...')}</div>
                </div>
            )}

            {/* Inline refresh indicator */}
            {loading && items.length > 0 && (
                <div className="flex items-center justify-center gap-2 py-2">
                    <Spinner size="sm" className="text-primary" />
                    <span className="text-xs text-muted-foreground">{t('Refreshing...')}</span>
                </div>
            )}

            {/* Search */}
            {!(loading && items.length === 0) && (
            <div className="flex justify-center mt-2.5 px-4">
                <div className="relative w-full max-w-[720px]">
                    <AppIcon
                        name="Search"
                        size={18}
                        className="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="text"
                        placeholder={t('Search')}
                        onChange={(e) => filterDataGlobal(e.target.value ?? '')}
                        className="w-full h-10 ltr:pl-10 rtl:pr-10 ltr:pr-3 rtl:pl-3 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                </div>
            </div>
            )}

            <div className="h-2" />

            {/* Country groups */}
            <div className="flex flex-col mx-4 mb-6">
                {countries.map((country) => {
                    const countryUsers = items.filter(
                        (u) => country.name === u.country?.name,
                    );
                    if (countryUsers.length === 0) return null;
                    return (
                        <div
                            key={country.iso}
                            className="border rounded-lg p-5 mb-4"
                            style={{
                                borderColor: brandColor,
                                backgroundColor: `${companyPrimaryColor ?? '#304EA1'}10`,
                            }}
                        >
                            <div
                                className="flex items-center gap-2 mb-2"
                                style={{ color: brandColor }}
                            >
                                <span className="font-bold">
                                    <ReactCountryFlag countryCode={country.iso} svg />
                                </span>
                                <span className="text-base font-bold">{country.name}</span>
                            </div>
                            <div className="flex flex-wrap justify-center gap-3">
                                {countryUsers.map((u: any) => {
                                    const statusText =
                                        u.currentCustomStatusText || u.currentStatus;
                                    return (
                                        <div
                                            key={u.userId || u.id}
                                            className="w-[266px] bg-transparent"
                                        >
                                            <div className="flex items-center gap-3 p-3">
                                                <div className="relative shrink-0">
                                                    <img
                                                        src={
                                                            u.imageUrl
                                                                ? getFullUrl(u.imageUrl) || '/images/defaultProfile.png'
                                                                : '/images/defaultProfile.png'
                                                        }
                                                        alt={u.fullName}
                                                        className="w-10 h-10 rounded-full object-cover"
                                                    />
                                                    <span
                                                        className={`absolute bottom-0 ltr:right-0 rtl:left-0 w-3 h-3 rounded-full border-2 border-white ${getStatusColor(statusText)}`}
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <p
                                                        className="font-bold text-[15px] truncate"
                                                        style={{ color: brandColor }}
                                                    >
                                                        {u.fullName}
                                                    </p>
                                                    <p
                                                        className={`text-xs ${getStatusTextColor(statusText)}`}
                                                    >
                                                        {t(statusText)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </PageLayout>
    );
}
